import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAgentRadar } from "../../hooks/agent-radar/useAgentRadar";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import RadarInfoDot from "../../components/agent-radar/RadarInfoDot";
import RadarEmptyState from "../../components/agent-radar/RadarEmptyState";
import ListsSidebar from "../../components/agent-radar/ListsSidebar";
import EntryCard from "../../components/agent-radar/EntryCard";
import EntryDetail from "../../components/agent-radar/EntryDetail";
import DeleteListModal from "../../components/agent-radar/DeleteListModal";
import ShareActionSheet from "../../components/common/ShareActionSheet";
import { agentRadarService } from "../../services/agentRadarService";
import {
  MobileListSheet,
  MobileEntrySheet,
  MobileFilterSheet,
  MobileNewListSheet,
  MobileListOptsSheet,
} from "../../components/agent-radar/RadarMobileSheets";
import { RADAR_POSITION_OPTIONS, RADAR_SORT_OPTIONS } from "../../constants/agentRadarChoices";
import { playersCountText, RADAR_TOAST_STYLE } from "../../components/agent-radar/radarFormat";

// TP Agent Radar — personal shortlists (P1). Mixed page; per the lead's container
// RULE the content container = the design source's 1240px (max-w-[1240px]),
// narrower than the 3-class row-list `screen-2xl` because the prototype is the
// authoritative source for this new surface. Desktop = lists sidebar + card grid
// with popovers; mobile (≤767px) = list dropdown + bottom sheets. Real player data
// only. Share entry points are P1 placeholders wired for P3.
const radarToast = (msg) => toast(msg, { position: "bottom-center", style: RADAR_TOAST_STYLE });

// Share-to-chat now runs through the platform STANDARD picker (ShareActionSheet,
// the same sheet player-profile / friendlies / last-minute use). This thin
// adapter maps the picker's selected recipients → the radar backend contract
// (POST agent/radar/share {type, entry_id|list_id, channel_ids[], message?}) —
// no fork of the shared component, no radar-specific picker. `target` shape:
//   { type: "player"|"list", entryId?, listId?, name, count? }
const RadarShareAdapter = ({ target, onClose }) => {
  const onShare = async (recipients, message) => {
    // ShareActionSheet keeps `channel_id_candidate` on each recipient (its
    // recipient transform); the backend does the Stream sends from channel ids.
    const channelIds = (recipients || [])
      .map((r) => r.channel_id_candidate || r.channel_id)
      .filter(Boolean);
    if (channelIds.length === 0) {
      toast.error("Open a chat with these contacts first, then share.");
      return { success: false };
    }
    const payload = {
      type: target.type,
      channel_ids: channelIds,
      message: message || "",
      ...(target.type === "player"
        ? { entry_id: target.entryId }
        : { list_id: target.listId }),
    };
    const res = await agentRadarService.share(payload);
    if (res?.success) {
      radarToast("Shared in chat");
      return { success: true };
    }
    if (res?.status === 403) {
      toast.error("You're not in one of these chats yet. Open it first, then share.");
    } else {
      toast.error(res?.error || "Could not share into the chat.");
    }
    return { success: false };
  };

  return (
    <ShareActionSheet
      isOpen={!!target}
      onClose={onClose}
      customShareHandler={onShare}
      title="Share to chat"
    />
  );
};

const AgentRadar = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const {
    lists,
    labels,
    entries,
    selectedList,
    setSelectedList,
    loading,
    listsLoading,
    error,
    filterPos,
    setFilterPos,
    filterLabel,
    setFilterLabel,
    sortBy,
    setSortBy,
    createList,
    renameList,
    deleteList,
    removeEntry,
    moveEntry,
    copyEntry,
    assignLabel,
    unassignLabel,
    createLabel,
    addNote,
  } = useAgentRadar();

  const [detailEntryId, setDetailEntryId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null); // P3 share sheet target
  // mobile sheet coordination
  const [mobileSheet, setMobileSheet] = useState(null); // 'list' | 'filter' | 'newlist'
  const [entrySheet, setEntrySheet] = useState(null); // entry object
  const [listOpts, setListOpts] = useState(null); // list object

  const totalCount = useMemo(() => lists.reduce((sum, l) => sum + (l.entry_count || 0), 0), [lists]);
  const radarEmpty = !listsLoading && totalCount === 0;

  const selectedMeta = lists.find((l) => l.id === selectedList) || null;
  const detailEntry = entries.find((e) => e.id === detailEntryId) || null;
  const filtersActive = filterPos !== "all" || filterLabel !== "all";

  const labelOptions = useMemo(
    () => [{ value: "all", label: "All labels" }, ...labels.map((l) => ({ value: l.id, label: l.name }))],
    [labels],
  );

  const closeSheets = () => { setMobileSheet(null); setEntrySheet(null); setListOpts(null); };

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleBrowse = () => navigate("/search");

  const handleCreateList = async (name) => {
    const res = await createList(name);
    if (res.success) radarToast(`List "${name}" created`);
    else toast.error(res.error || "Could not create the list.");
    closeSheets();
  };

  const handleRenameList = async (id, name) => {
    const res = await renameList(id, name);
    if (!res.success) toast.error(res.error || "Could not rename the list.");
  };

  const handleConfirmDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    closeSheets();
    const res = await deleteList(target.id);
    if (res.success) radarToast("List deleted — players moved to My Radar");
    else toast.error(res.error || "Could not delete the list.");
  };

  const handleMove = async (entry, list) => {
    closeSheets();
    const res = await moveEntry(entry.id, list.id);
    if (res.success) radarToast(`Moved to "${list.name}"`);
    else toast.error(res.error || "Could not move the player.");
  };

  const handleCopy = async (entry, list) => {
    closeSheets();
    const res = await copyEntry(entry.id, list.id);
    if (res.success) radarToast(`Copied to "${list.name}"`);
    else toast.error(res.error || "Could not copy the player.");
  };

  const handleRemove = async (entry) => {
    closeSheets();
    if (detailEntryId === entry.id) setDetailEntryId(null);
    const res = await removeEntry(entry.id);
    if (res.success) radarToast("Removed from your radar");
    else toast.error(res.error || "Could not remove the player.");
  };

  // Share to chat: entry ⋯ "Share to chat" → single-player card; list header
  // "Share list" → shortlist snapshot. RadarShareAdapter routes the pick through
  // the platform-standard ShareActionSheet, then POSTs the radar share.
  const handleShareEntry = (entry) => {
    closeSheets();
    setShareTarget({ type: "player", entryId: entry.id, name: entry.name });
  };
  const handleShareList = () => {
    if (!selectedMeta) return;
    setShareTarget({
      type: "list",
      listId: selectedMeta.id,
      name: selectedMeta.name,
      count: selectedMeta.entry_count ?? entries.length,
    });
  };

  const handleOpenProfile = (entry) => {
    // Coach entries (Coach-Radar Phase 1) route to the coach profile; the
    // public player profile keys on the ws id as before.
    if (entry.ts_coach_id) {
      navigate(`/search/coach/${entry.ts_coach_id}`);
      return;
    }
    const pid = entry.ws_player_id || entry.player_id;
    if (pid) navigate(`/search/player/${pid}`);
  };

  // ── shared pieces ────────────────────────────────────────────────────────────
  const cardProps = (entry) => ({
    entry,
    lists,
    selectedList,
    onOpen: (e) => setDetailEntryId(e.id),
    onMove: handleMove,
    onCopy: handleCopy,
    onShare: handleShareEntry,
    onRemove: handleRemove,
    onOpenActions: (e) => setEntrySheet(e),
  });

  const emptyPane = (
    <>
      {entries.length === 0 && !loading && (
        filtersActive ? (
          <div className="text-center text-[12.5px] text-[#9CA3AF]" style={{ padding: isMobile ? "36px 16px" : "40px 0" }}>
            No players match your filters.
          </div>
        ) : (
          <div
            className={isMobile ? "mx-4 my-1.5" : ""}
            style={{ background: "#FFFFFF", border: "1px solid #E4E9F0", borderRadius: 14, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", padding: isMobile ? "44px 22px" : "56px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 7, textAlign: "center" }}
          >
            <div className="flex items-center justify-center" style={{ width: isMobile ? 42 : 44, height: isMobile ? 42 : 44, borderRadius: "50%", border: "1.5px solid #DDE3EB", marginBottom: isMobile ? 4 : 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#C7D0DC" }} />
            </div>
            <div className="text-[14.5px] font-bold text-[#111827]">No players in this list yet</div>
            <div className="text-[12.5px] text-[#6B7280] leading-[1.55]" style={{ maxWidth: 340 }}>
              Add players from any player profile, or move entries here with an entry&apos;s ⋯ menu.
            </div>
          </div>
        )
      )}
    </>
  );

  // ── mobile ───────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#EDF0F4]">
        <div className="flex items-center" style={{ gap: 2, padding: "10px 16px 4px" }}>
          <div className="text-[24px] font-bold text-[#111827]">Radar</div>
          <RadarInfoDot mobile />
        </div>

        {radarEmpty ? (
          <RadarEmptyState mobile onBrowse={handleBrowse} />
        ) : (
          <>
            <div style={{ padding: "8px 16px 4px" }}>
              <button
                type="button"
                onClick={() => setMobileSheet("list")}
                className="w-full bg-white flex items-center cursor-pointer"
                style={{ height: 48, border: "1px solid #DDE3EB", borderRadius: 9, padding: "0 14px", gap: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}
              >
                <span className="text-[14px] font-bold text-[#111827] overflow-hidden text-ellipsis whitespace-nowrap">{selectedMeta?.name || "My Radar"}</span>
                <span className="text-[12px] font-semibold text-[#9CA3AF] flex-shrink-0">{selectedMeta?.entry_count ?? 0}</span>
                <span className="ml-auto text-[#9CA3AF] text-[12px] flex-shrink-0">▾</span>
              </button>
            </div>

            <div className="flex items-center" style={{ gap: 8, padding: "8px 16px 10px" }}>
              <div className="text-[12px] text-[#9CA3AF] font-semibold flex-1">{playersCountText(entries.length)}</div>
              {entries.length > 0 && (
                <button type="button" onClick={handleShareList} className="bg-white text-[#111827] font-semibold cursor-pointer whitespace-nowrap" style={{ height: 44, padding: "0 14px", border: "1px solid #DDE3EB", borderRadius: 9, fontSize: 13 }}>Share</button>
              )}
              <button
                type="button"
                onClick={() => setMobileSheet("filter")}
                className="font-semibold cursor-pointer whitespace-nowrap"
                style={{ height: 44, padding: "0 14px", border: filtersActive ? "1px solid #2283FF" : "1px solid #DDE3EB", borderRadius: 9, fontSize: 13, background: filtersActive ? "#EAF2FF" : "#FFFFFF", color: filtersActive ? "#1B6FE0" : "#374151" }}
              >
                Filter &amp; sort{filtersActive ? ` (${(filterPos !== "all" ? 1 : 0) + (filterLabel !== "all" ? 1 : 0)})` : ""}
              </button>
            </div>

            {error && <div className="text-center text-[12.5px] text-[#DC2626]" style={{ padding: "20px 16px" }}>{error}</div>}
            {emptyPane}
            <div className="flex flex-col" style={{ gap: 10, padding: "0 16px" }}>
              {entries.map((entry) => (
                <EntryCard key={entry.id} mobile {...cardProps(entry)} />
              ))}
            </div>
          </>
        )}

        {/* sheets */}
        {mobileSheet === "list" && (
          <MobileListSheet
            lists={lists}
            selectedList={selectedList}
            onSelect={(id) => { setSelectedList(id); setMobileSheet(null); }}
            onNewList={() => setMobileSheet("newlist")}
            onOpenOpts={(l) => { setMobileSheet(null); setListOpts(l); }}
            onClose={() => setMobileSheet(null)}
          />
        )}
        {mobileSheet === "newlist" && (
          <MobileNewListSheet onCreate={handleCreateList} onClose={() => setMobileSheet(null)} />
        )}
        {mobileSheet === "filter" && (
          <MobileFilterSheet
            posOptions={RADAR_POSITION_OPTIONS}
            labelOptions={labelOptions}
            sortOptions={RADAR_SORT_OPTIONS}
            filterPos={filterPos}
            filterLabel={filterLabel}
            sortBy={sortBy}
            setFilterPos={setFilterPos}
            setFilterLabel={setFilterLabel}
            setSortBy={setSortBy}
            onClose={() => setMobileSheet(null)}
          />
        )}
        {listOpts && (
          <MobileListOptsSheet
            list={listOpts}
            onRename={handleRenameList}
            onDeleteRequest={(l) => { setListOpts(null); setDeleteTarget(l); }}
            onClose={() => setListOpts(null)}
          />
        )}
        {entrySheet && (
          <MobileEntrySheet
            entry={entrySheet}
            lists={lists}
            selectedList={selectedList}
            onMove={handleMove}
            onCopy={handleCopy}
            onShare={handleShareEntry}
            onRemove={handleRemove}
            onClose={() => setEntrySheet(null)}
          />
        )}
        {detailEntry && (
          <EntryDetail
            entry={detailEntry}
            mobile
            lists={lists}
            ownerLabels={labels}
            onClose={() => setDetailEntryId(null)}
            onOpenProfile={handleOpenProfile}
            onAssignLabel={assignLabel}
            onUnassignLabel={unassignLabel}
            onCreateLabel={createLabel}
            onAddNote={addNote}
          />
        )}
        {deleteTarget && (
          <DeleteListModal mobile listName={deleteTarget.name} onCancel={() => setDeleteTarget(null)} onConfirm={handleConfirmDelete} />
        )}
        <RadarShareAdapter target={shareTarget} onClose={() => setShareTarget(null)} />
      </div>
    );
  }

  // ── desktop ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6" style={{ paddingTop: 20, paddingBottom: 56 }}>
        <div className="flex items-center relative" style={{ gap: 9, margin: "6px 0 18px" }}>
          <div className="text-[22px] font-bold text-[#111827]">Radar</div>
          <RadarInfoDot />
        </div>

        {radarEmpty ? (
          <RadarEmptyState onBrowse={handleBrowse} />
        ) : (
          <div className="grid items-start" style={{ gridTemplateColumns: "260px 1fr", gap: 20 }}>
            <ListsSidebar
              lists={lists}
              selectedList={selectedList}
              onSelect={setSelectedList}
              onCreateList={handleCreateList}
              onRenameList={handleRenameList}
              onDeleteRequest={(l) => setDeleteTarget(l)}
            />

            <div className="min-w-0">
              <div className="flex items-center flex-wrap" style={{ gap: 8, marginBottom: 12 }}>
                <RadarSelect value={filterPos} onChange={setFilterPos} options={RADAR_POSITION_OPTIONS} />
                <RadarSelect value={filterLabel} onChange={setFilterLabel} options={labelOptions} />
                <RadarSelect value={sortBy} onChange={setSortBy} options={RADAR_SORT_OPTIONS} />
                <div className="flex items-center" style={{ marginLeft: "auto", gap: 10 }}>
                  <div className="text-[11.5px] text-[#9CA3AF] font-semibold">{playersCountText(entries.length)}</div>
                  {entries.length > 0 && (
                    <button type="button" onClick={handleShareList} className="bg-white hover:bg-[#F8FAFC] text-[#111827] font-semibold cursor-pointer whitespace-nowrap flex-shrink-0" style={{ border: "1px solid #DDE3EB", borderRadius: 9, padding: "7px 13px", fontSize: 12.5 }}>Share list</button>
                  )}
                </div>
              </div>

              {error && <div className="text-center text-[12.5px] text-[#DC2626]" style={{ padding: "40px 0" }}>{error}</div>}
              {!error && emptyPane}
              {entries.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(310px,1fr))", gap: 12 }}>
                  {entries.map((entry) => (
                    <EntryCard key={entry.id} {...cardProps(entry)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {detailEntry && (
        <EntryDetail
          entry={detailEntry}
          lists={lists}
          ownerLabels={labels}
          onClose={() => setDetailEntryId(null)}
          onOpenProfile={handleOpenProfile}
          onAssignLabel={assignLabel}
          onUnassignLabel={unassignLabel}
          onCreateLabel={createLabel}
          onAddNote={addNote}
        />
      )}
      {deleteTarget && (
        <DeleteListModal listName={deleteTarget.name} onCancel={() => setDeleteTarget(null)} onConfirm={handleConfirmDelete} />
      )}
      <RadarShareAdapter target={shareTarget} onClose={() => setShareTarget(null)} />
    </div>
  );
};

// Native select styled to the design source (height 34, #DDE3EB border, 12.5px).
const RadarSelect = ({ value, onChange, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="cursor-pointer outline-none"
    style={{ height: 34, border: "1px solid #DDE3EB", borderRadius: 9, background: "#FFFFFF", color: "#374151", fontSize: 12.5, padding: "0 9px" }}
  >
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

export default AgentRadar;
